import { ResponseBody } from '@gongxigames/framework/integrations/rubyplay/types';
import { RubyPlayGameClientWrapper } from '@gongxigames/framework/integrations/rubyplay/RubyPlayGameClientWrapper';
import { dateDiffInSeconds } from '@gongxigames/framework/slot';
import {
    PromotionTypes,
    IPromotionalStatusesInterface,
    ISingleSetPromotionInterface,
    PromotionBalanceEntry,
    PromotionBalanceEntryQualifier,
    PromotionBalanceExtractor,
    SingleSetPromotionChoice,
} from '@gongxigames/framework/promotions';

export type SingleSetPromotionInitData = {
    clientWrapper: RubyPlayGameClientWrapper;
    initResponse: ResponseBody;
    defaultBet: number;
};

const INITIAL_PROMOTION_BALANCE: PromotionBalanceEntry = {
    awarded: 0,
    remained: 0,
    type: 'free-round',
    id: '',
    bet: 0,
    collectedWin: 0,
};

export class SingleSetPromotion implements ISingleSetPromotionInterface, IPromotionalStatusesInterface {
    public static PING_BALANCE_DELAY = 3000;
    private defaultBet!: number;
    protected promotionBalance: PromotionBalanceEntry = { ...INITIAL_PROMOTION_BALANCE };
    private activePromotionId?: string;
    private rejected = false;
    private clientWrapper!: RubyPlayGameClientWrapper;

    public init({ initResponse, clientWrapper, defaultBet }: SingleSetPromotionInitData): void {
        const { player, active_promotion_id } = initResponse.data;
        const promotionBalance: PromotionBalanceEntry | undefined = PromotionBalanceExtractor.extractFromPlayer(player)[0];

        this.clientWrapper = clientWrapper;
        this.defaultBet = defaultBet;

        if (active_promotion_id) {
            this.activePromotionId = active_promotion_id;
        }

        if (promotionBalance) {
            this.promotionBalance = promotionBalance;
        }
    }

    public update(response: ResponseBody): void {
        if (this.rejected) {
            return;
        }

        const { player, active_promotion_id = '' } = response.data;
        const promotionBalance: PromotionBalanceEntry | undefined = PromotionBalanceExtractor.extractFromPlayer(player)[0];

        if (promotionBalance) {
            this.promotionBalance = promotionBalance;

            if (active_promotion_id) {
                this.activePromotionId = active_promotion_id;
            }
        }
    }

    public getQualifier(): PromotionBalanceEntryQualifier {
        return this.promotionBalance.type;
    }

    public subtractPromotion(): void {
        this.promotionBalance.remained -= 1;
    }

    public end(): void {
        this.activePromotionId = undefined;
        this.promotionBalance = { ...INITIAL_PROMOTION_BALANCE };
        this.clientWrapper.updateDefaultBet();
    }

    public hasPromotion(): boolean {
        return this.isQueued(this.promotionBalance) || this.isInUse(this.promotionBalance);
    }

    public getType(): PromotionTypes {
        return PromotionTypes.SingleSetPromotion;
    }

    public getBet(): number {
        return this.promotionBalance.bet || this.defaultBet;
    }

    public getID(): string {
        return this.promotionBalance.id;
    }

    public getPlayedRounds(): number {
        return 0;
    }

    public getRemainedRounds(): number {
        return this.promotionBalance.remained;
    }

    public getTotalRounds(): number {
        return this.promotionBalance.remained;
    }

    public getWin(): number {
        return this.promotionBalance.collectedWin || this.defaultBet;
    }

    public isActive(): boolean {
        return !this.rejected && this.activePromotionId !== undefined && this.activePromotionId === this.promotionBalance.id;
    }

    public isCurrentInUse(): boolean {
        return this.isInUse(this.promotionBalance);
    }

    public isCurrentExpired(): boolean {
        return this.isActive() && this.isExpired(this.promotionBalance);
    }

    public setChoice(choice: SingleSetPromotionChoice, callback: () => void): void {
        const promotionDecision = choice === 'accept' ? 'accept' : 'ask_me_later';

        this.rejected = choice === 'reject';

        this.clientWrapper.sendPromotionDecision(
            promotionDecision,
            () => {
                if (this.rejected) {
                    this.promotionBalance = { ...INITIAL_PROMOTION_BALANCE };
                } else {
                    this.activePromotionId = this.promotionBalance.id;
                    this.clientWrapper.updateDefaultBet();
                }

                callback();
            },
            () => {}
        );
    }

    public getPingBalanceDelay(): number {
        return SingleSetPromotion.PING_BALANCE_DELAY;
    }

    public getAdditionalFieldsForServerRequest(): Record<string, any> | undefined {
        return undefined;
    }

    public isQueued(balance: PromotionBalanceEntry): boolean {
        return balance.status === 'QUEUED';
    }

    public isInUse(balance: PromotionBalanceEntry): boolean {
        const inUse = balance.status === 'IN_USE' || balance.remained > 0;
        const queued = this.isQueued(balance);

        return inUse && !queued;
    }

    public isCompleted(balance: PromotionBalanceEntry): boolean {
        return balance.status === 'COMPLETED' || balance.remained === 0;
    }

    public isExpired(balance: PromotionBalanceEntry): boolean {
        const isExpired = dateDiffInSeconds(Date.now(), new Date(`${balance.expirationTime}Z`).getTime()) < 0;

        return balance.status === 'EXPIRED' || isExpired;
    }

    public isOptOut(balance: PromotionBalanceEntry): boolean {
        return balance.status === 'OPT_OUT';
    }

    public isDisabled(balance: PromotionBalanceEntry): boolean {
        return balance.status === 'DISABLED';
    }
}
